import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { planPropTypes } from 'common/propTypes';
import { DOMAINS } from 'common/constants/index';
import { initiateUserAuth } from 'common/auth/modalLogin';
import { reactToastify, TOASTIFY_TYPES } from 'common/utils/reactToastify';
import ModalComponent from 'common/components/ModalComponent';
import { paymentInitialize } from 'common/razorpay/paymentInitialize';
import classNames from 'classnames';
import { cyclicPlansPeriods } from 'ui/Plans/config';
import PlanPurchaseTypeSelectionModal from './PlanPurchaseTypeSelectionModal';
import PaymentCoupons from './PaymentCoupons';
import PlanMultiplierModal from './PlanMultiplierModal';
import ApplicationDomainUtility from 'v3/common/utils/applicationDomain';
import Button from 'v3/common/components/Button';
import { isCashfreePg } from 'ui/QuotaVerification/helper';
import moment from 'moment-timezone';
import { ACTIVE_BROKERS_DOMAINS } from 'v3/pages/LandingPage/Default/BannerSection/types.type';

const propTypes = {
  plan: planPropTypes.isRequired,
  activePlanId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  couponConfig: PropTypes.shape({ code: PropTypes.string }),
  activePlanName: PropTypes.string,
  isSubscriptionActive: PropTypes.bool.isRequired,
  isCreditApplied: PropTypes.bool,
  couponCode: PropTypes.string.isRequired,
  onChangeCouponCode: PropTypes.func.isRequired,
  isApplied: PropTypes.bool.isRequired,
  onCouponUpdate: PropTypes.func.isRequired,
  message: PropTypes.string.isRequired,
  btnClassName: PropTypes.string,
  isHideIcon: PropTypes.bool,
  plansGroupedByCyclicMonths: PropTypes.oneOfType([PropTypes.array, PropTypes.shape({})]),
  btnLabel: PropTypes.string,
  paymentCallBack: PropTypes.func,
};
const defaultProps = {
  activePlanId: null,
  couponConfig: {},
  activePlanName: '',
  isCreditApplied: false,
  btnClassName: '',
  isHideIcon: false,
  plansGroupedByCyclicMonths: {},
  btnLabel: null,
};

const PlanButton = ({
  plan,
  activePlanId,
  couponConfig,
  activePlanName,
  isSubscriptionActive,
  isCreditApplied,
  couponCode,
  onChangeCouponCode,
  isApplied,
  onCouponUpdate,
  message,
  btnClassName,
  isHideIcon,
  plansGroupedByCyclicMonths,
  btnLabel,
  paymentCallBack,
}) => {
  const [selectedPlan, setSelectedPlan] = useState(plan);
  const [isLoading, setIsLoading] = useState(false);
  const {
    id,
    currentNetPrice,
    price,
    plan_name: planName,
    is_premium: isPremium,
    is_pro: isPro,
    cycle_duration_in_months: cycleMonth,
  } = selectedPlan;
  const { hasPaymentEnabled, currentDomain } = window;
  const coupenCode = _.get(couponConfig, 'code', '');
  const isCouponAvailable = !_.isEmpty(couponConfig);
  const [isUserSignedIn, setIsUserSignedIn] = useState(window.isUserSignedIn);
  const [modalConfigs, setModalConfigs] = useState({
    isShowPurchaseTypeModal: false,
    isShowConfirmationModal: false,
    isShowMultiplierModal: false,
  });
  const [purchaseType, setPurchaseType] = useState('payment');
  const paramCouponCode = currentNetPrice === price ? '' : coupenCode;
  const isEmailUser = _.get(window, 'isEmailUser', false);
  const {
    yearly: { period: yearlyPeriod },
  } = cyclicPlansPeriods;
  const isYearlyPlan = cycleMonth % yearlyPeriod === 0 || false;

  useEffect(() => {
      if (ApplicationDomainUtility.isB2b || isCashfreePg || (isCouponAvailable && purchaseType !== 'payment')) {
      setPurchaseType('payment');
    } else {
      setPurchaseType('subscription');
    }
  }, [couponConfig]);

  useEffect(() => {
    setSelectedPlan(plan);
  }, [plan]);

  const { isShowPurchaseTypeModal, isShowMultiplierModal } = modalConfigs;

  const createPayment = async () => {    
    setModalConfigs({ isShowPurchaseTypeModal: false, isShowMultiplierModal: false });
    setSelectedPlan(plan);
    const args = {
      plan_id: id,
      purchase_type: purchaseType,
      coupon_code: paramCouponCode,
      credits_applied: isCreditApplied,
      plan_name: planName,
      price: currentNetPrice,
    };
    paymentInitialize(
      args,
      (paymentResponse) => {
        // if payment callback is passed from parent then no further action needed just return the response
        if (paymentCallBack && paymentResponse) return paymentCallBack(paymentResponse);
        // this is a manual trigger for payment success GTM event
        if (window.trackEvent) window.trackEvent('User', 'payment_success', `Payment Success-${id}`);
        setTimeout(() => {
          window.location.reload();
          setIsLoading(false);
        }, 1.5 * 1000);
      },
      purchaseType === 'subscription',
      setIsLoading
    );
  };

  const onSubmit = () => {
    setIsLoading(true);

    if (isUserSignedIn && !isEmailUser) return createPayment();

    if (!isUserSignedIn) {
      initiateUserAuth({ action: 'plan_purchase' })
        .then((res) => {
          setIsUserSignedIn(true);
          window.location.href = res?.href;
        })
        .catch(() => {
          reactToastify('Unauthorized, Please login and Continue', TOASTIFY_TYPES.ERROR);
        });
    }

    return createPayment();
  };

  // Algoverse specific check for service availability between 5:30 AM to 12:00 AM IST
  const isFoundServiceAvailable = () => {
    const now = moment().tz('Asia/Kolkata');

    const SERVICE_START = moment.tz('05:30', 'HH:mm', 'Asia/Kolkata');
    const SERVICE_END = moment.tz('23:59', 'HH:mm', 'Asia/Kolkata');

    return now.isBetween(SERVICE_START, SERVICE_END, null, '[)');
  };

  const onHandlePaymentButton = () => {
    if (ApplicationDomainUtility.isAlgoverse && !isFoundServiceAvailable()) {
      reactToastify(
        'The Funds service is accessible from 5:30 AM to 12:00 AM IST daily. Please try again during these service hours.',
        TOASTIFY_TYPES.ERROR
      );
      return;
    }
    
    if (
      ApplicationDomainUtility.isB2b ||
      isCashfreePg ||
      ((isCouponAvailable || isCreditApplied) && !_.includes(
        [
          DOMAINS.DEFAULT_DOMAIN,
          // This is added coz of inhouse hosting , please ask for further assistance if you want to know more about this.
          ACTIVE_BROKERS_DOMAINS.ALICEBLUE
        ], currentDomain))
    ) {
      onSubmit();
      return;
    }
    if (isYearlyPlan) {
      setModalConfigs({ ...modalConfigs, isShowMultiplierModal: true });
      return;
    }
    setModalConfigs({ ...modalConfigs, isShowPurchaseTypeModal: true });
  };

  const renderPurchaseTypeComponent = () => {
    return (
      <>
        <div className="mb-5 billing-type">
          <div className="d-flex align-items-center gap-10">
            <div className="alert-icons alert-info icon-payment mb-0 tx-25"></div>

            <h4 className="font-weight-semi mb-0">Billing Type</h4>
          </div>
          <PaymentCoupons
            couponCode={couponCode}
            onChangeCouponCode={onChangeCouponCode}
            isApplied={isApplied}
            onCouponUpdate={onCouponUpdate}
            message={message}
          />
        </div>

        <div className="d-flex flex-column gap-25">
          <PlanPurchaseTypeSelectionModal
            purchaseType={purchaseType}
            onChangePurchaseType={setPurchaseType}
          />
        </div>
      </>
    );
  };

  // This is wrong to be moved when migrating to V3
  const isDisabled = ApplicationDomainUtility.isAlgoverse
    ? false
    : !hasPaymentEnabled || (activePlanName && activePlanName !== planName) || isSubscriptionActive;

  const buttonClassName = btnClassName
    ? classNames(btnClassName, { disabled: isDisabled })
    : classNames({ 'premium-btn': isPremium, 'basic-btn': !isPremium, disabled: isDisabled });

  // TODO: Fix this when migrating to V3
  // Very bad for now, coz many css needed to be rewamped
  // Works for now
  const ButtonComponent = (ApplicationDomainUtility.isAlgoverse) ? Button : 'button';

  return (
    <>
      <React.Fragment key={id}>
        <form id={`planForm${id}`} action="/payment/init_payment" method="post" className="d-none">
          <input type="hidden" value={id} name="plan_id" />
          <input type="hidden" value={purchaseType} name="purchase_type" />
          <input type="hidden" value={paramCouponCode || ''} name="coupon_code" />
        </form>
        <ButtonComponent
          isLoading={isLoading}
          className={buttonClassName}
          type="button"
          disabled={isDisabled}
          onClick={() => {
            onHandlePaymentButton(plan);
          }}
        >
          {btnLabel ?? (activePlanId ? 'Extend Now' : 'Order Now')}
          {!isHideIcon && <i className="fa-solid fa-circle-arrow-right ms-2" />}
        </ButtonComponent>
      </React.Fragment>
      {isShowPurchaseTypeModal && (
        <ModalComponent
          onSave={() => {
            onSubmit();
          }}
          onClose={() => {
            setModalConfigs({ ...modalConfigs, isShowPurchaseTypeModal: false });
          }}
          title=""
          okBtnTitle="Order Now"
          btnClassName="mr-auto"
          size="lg"
        >
          {renderPurchaseTypeComponent()}
        </ModalComponent>
      )}
      {isShowMultiplierModal && (
        <PlanMultiplierModal
          onChangeSelectedPlan={setSelectedPlan}
          planName={planName}
          onShowPurchaseTypeModal={() => setModalConfigs({ ...modalConfigs, isShowPurchaseTypeModal: true })}
          onCloseModal={() => {
            setModalConfigs({ ...modalConfigs, isShowMultiplierModal: false });
            setSelectedPlan(plan);
          }}
          isPremium={isPremium}
          isPro={isPro}
          plansGroupedByCyclicMonths={plansGroupedByCyclicMonths}
        />
      )}
    </>
  );
};

PlanButton.propTypes = propTypes;
PlanButton.defaultProps = defaultProps;

export default PlanButton;
